import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';
import { PhoneIcon, MicIcon, MailIcon, CalendarIcon, DatabaseIcon, InboxIcon, ArrowRightIcon, CheckCircleIcon, MessageSquareIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
export function AutomationsPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-408">
      <SEOHead title="Marketing Automations | CRM & Lead Follow-Up Systems | Viridia Analytics Phoenix" description="Never miss a lead again. Smart marketing automations including missed-call text back, automated follow-up sequences, CRM integration, and appointment booking for Phoenix service businesses." canonical="/automations" keywords="marketing automation Phoenix, CRM integration, missed call text back, lead follow-up automation, appointment booking system, lead nurturing, business automation Phoenix" data-id="element-409" />
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden min-h-[90vh] flex items-center" data-id="element-410">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-411">
          <div className="absolute top-[20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-412"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-413">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-414">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-415">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-416">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-semibold tracking-wide mb-6" data-id="element-417">
                  <span className="w-2 h-2 rounded-full bg-viridia mr-2" data-id="element-418"></span>
                  BUILT FOR BUSINESS OWNERS
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-419">
                  Win Back Your{' '}
                  <span className="text-viridia" data-id="element-420">Evenings & Weekends.</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-421">
                  We replace manual busywork with smart systems that talk to
                  your customers, book your jobs, and keep your business
                  organized 24/7.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-422">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-423">
                    Fix My Business Workflow
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-424" />
                  </Link>
                  <a href="#features" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-425">
                    See How It Works
                  </a>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration */}
            <div className="relative h-[400px] lg:h-[500px] hidden md:block" data-id="element-426">
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4" data-id="element-427">
                <motion.div initial={{
                opacity: 0,
                x: 50
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} className="bg-viridia-surface border border-gray-800 p-4 rounded-2xl shadow-xl w-80 self-start ml-10 relative" data-id="element-428">
                  <div className="flex items-center gap-3" data-id="element-429">
                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 flex-shrink-0" data-id="element-430">
                      <PhoneIcon className="w-5 h-5" data-id="element-431" />
                    </div>
                    <div className="flex-1" data-id="element-432">
                      <p className="text-white font-medium text-sm" data-id="element-433">
                        Missed Call: Auto-Responding...
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-434">Just now</p>
                    </div>
                    <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-435" />
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: -50
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: 0.6
              }} className="bg-viridia-surface border border-viridia/30 p-4 rounded-2xl shadow-[0_0_20px_rgba(16,185,129,0.1)] w-80 self-end mr-10 relative" data-id="element-436">
                  <div className="flex items-center gap-3" data-id="element-437">
                    <div className="w-10 h-10 rounded-full bg-viridia/20 flex items-center justify-center text-viridia flex-shrink-0" data-id="element-438">
                      <MessageSquareIcon className="w-5 h-5" data-id="element-439" />
                    </div>
                    <div className="flex-1" data-id="element-440">
                      <p className="text-white font-medium text-sm" data-id="element-441">
                        "Hi! I can help you book that..."
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-442">
                        Automated SMS sent
                      </p>
                    </div>
                    <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-443" />
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                y: 50
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 1.2
              }} className="bg-viridia-surface border border-gray-800 p-4 rounded-2xl shadow-xl w-80 self-center relative mt-4" data-id="element-444">
                  <div className="flex items-center gap-3" data-id="element-445">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 flex-shrink-0" data-id="element-446">
                      <CalendarIcon className="w-5 h-5" data-id="element-447" />
                    </div>
                    <div className="flex-1" data-id="element-448">
                      <p className="text-white font-medium text-sm" data-id="element-449">
                        Job Booked & Sync'd to CRM
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-450">Added to calendar</p>
                    </div>
                    <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-451" />
                  </div>
                </motion.div>

                {/* Connecting lines */}
                <svg className="absolute inset-0 w-full h-full -z-10 pointer-events-none" style={{
                filter: 'drop-shadow(0 0 4px rgba(16,185,129,0.5))'
              }} data-id="element-452">
                  <motion.path d="M 120 150 C 120 200, 280 180, 280 230" fill="transparent" stroke="#10B981" strokeWidth="2" strokeDasharray="4 4" initial={{
                  pathLength: 0,
                  opacity: 0
                }} animate={{
                  pathLength: 1,
                  opacity: 0.5
                }} transition={{
                  duration: 1,
                  delay: 0.8
                }} data-id="element-453" />
                  <motion.path d="M 280 280 C 280 330, 200 310, 200 360" fill="transparent" stroke="#10B981" strokeWidth="2" strokeDasharray="4 4" initial={{
                  pathLength: 0,
                  opacity: 0
                }} animate={{
                  pathLength: 1,
                  opacity: 0.5
                }} transition={{
                  duration: 1,
                  delay: 1.4
                }} data-id="element-454" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE COST OF SLOW RESPONSE */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-455">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-456">
          <ScrollReveal data-id="element-457">
            <div className="text-center mb-12" data-id="element-458">
              <p className="text-sm font-bold text-red-400 tracking-widest uppercase mb-4" data-id="element-459">
                THE HARD TRUTH
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight" data-id="element-460">
                Every Missed Call Is a Job You{' '}
                <span className="text-red-400" data-id="element-461">Already Paid For.</span>
              </h2>
            </div>
          </ScrollReveal>

          <div className="space-y-6 max-w-3xl mx-auto" data-id="element-462">
            <ScrollReveal delay={0.1} data-id="element-463">
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed" data-id="element-464">
                You're spending money on ads. The phone rings. You're on a
                ladder, under a sink, or in the middle of a job.{' '}
                <span className="text-white font-semibold" data-id="element-465">
                  That call goes to voicemail — and that lead calls the next
                  guy.
                </span>
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-466">
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed" data-id="element-467">
                It's not a maybe.{' '}
                <span className="text-white font-semibold" data-id="element-468">
                  If you don't respond within 5 minutes, your chances of closing
                  that lead drop by 80%.
                </span>{' '}
                Most service businesses take hours. Some never call back at all.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-469">
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed" data-id="element-470">
                Meanwhile, your competitor down the street has a system that
                texts back instantly, books the appointment, and confirms it —
                all before you even saw the missed call notification.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.4} data-id="element-471">
              <div className="mt-10 bg-viridia-surface border border-gray-700 rounded-2xl p-8 text-center" data-id="element-472">
                <p className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-473">
                  You don't have a lead problem.
                </p>
                <p className="text-2xl md:text-3xl font-bold text-viridia" data-id="element-474">
                  You have a speed problem.
                </p>
                <p className="text-gray-400 mt-4 text-lg" data-id="element-475">
                  And it's costing you thousands every month in jobs that should
                  have been yours.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WHAT WE ACTUALLY DO SECTION */}
      <section id="features" className="py-24 bg-viridia-light" data-id="element-476">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-477">
          <ScrollReveal data-id="element-478">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-479">
              <p className="text-sm font-bold text-viridia tracking-widest uppercase mb-3" data-id="element-480">
                WHAT WE ACTUALLY DO
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-481">
                Tools that make your life easier.
              </h2>
              <p className="text-lg text-gray-600" data-id="element-482">
                You don't need to be a tech person. We build the systems so you
                can focus on your trade.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-483">
            {[{
            icon: PhoneIcon,
            title: 'Missed Call Text-Back',
            desc: 'Every missed call gets an instant text response. Never lose a lead because you were on a job.'
          }, {
            icon: MicIcon,
            title: 'AI Voice Receptionist',
            desc: "An AI-powered backup that answers calls, takes messages, and routes inquiries when you can't pick up."
          }, {
            icon: MailIcon,
            title: 'Automated Follow-Up',
            desc: "Smart email and SMS sequences that nurture leads until they're ready to book — without you lifting a finger."
          }, {
            icon: CalendarIcon,
            title: 'Online Booking',
            desc: 'Let customers book directly from your website, Google listing, or text messages. Syncs to your calendar automatically.'
          }, {
            icon: DatabaseIcon,
            title: 'CRM & Pipeline',
            desc: 'Track every lead, every job, every interaction in one place. Know exactly where every opportunity stands.'
          }, {
            icon: InboxIcon,
            title: 'Unified Inbox',
            desc: 'All your messages — texts, emails, Facebook DMs, Google messages — in one dashboard.'
          }].map((feature, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-484">
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full group" data-id="element-485">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 group-hover:bg-viridia/10 flex items-center justify-center text-gray-600 group-hover:text-viridia transition-colors duration-300 mb-6" data-id="element-486">
                    <feature.icon className="w-6 h-6" data-id="element-487" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-488">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-489">
                    {feature.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-490">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-491">
          <ScrollReveal data-id="element-492">
            <div className="text-center mb-16" data-id="element-493">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-494">
                From Missed Call to Booked Job in Minutes
              </h2>
            </div>
          </ScrollReveal>

          <div className="relative" data-id="element-495">
            {/* Connecting Line for Desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-800 -translate-y-1/2 z-0" data-id="element-496"></div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10" data-id="element-497">
              {[{
              step: '01',
              title: 'Customer Calls',
              desc: 'A potential customer calls your business number.'
            }, {
              step: '02',
              title: 'Instant Response',
              desc: 'If you miss it, they get an automatic text within seconds.'
            }, {
              step: '03',
              title: 'AI Takes Over',
              desc: 'Our AI assistant qualifies the lead and offers booking options.'
            }, {
              step: '04',
              title: 'Job Booked',
              desc: 'The appointment is confirmed and synced to your CRM and calendar.'
            }].map((item, index) => <ScrollReveal key={index} delay={index * 0.15} data-id="element-498">
                  <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 relative h-full" data-id="element-499">
                    <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-viridia text-white font-bold flex items-center justify-center shadow-lg" data-id="element-500">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 mt-2" data-id="element-501">
                      {item.title}
                    </h3>
                    <p className="text-gray-400" data-id="element-502">{item.desc}</p>
                  </div>
                </ScrollReveal>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-503">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-504"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-505">
          <ScrollReveal data-id="element-506">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-507">
              Stop Losing Leads to Voicemail
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-508">
              Let us build a system that works while you sleep. Book a free
              workflow audit and see what you're missing.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-509">
              Fix My Business Workflow
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-510" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}